import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import api from "../services/api";
import config from "../config";
import { menuItems, secondaryMenuItems } from "../config/menuItems";

const Sidebar = () => {
  const { logout, auth, empresa } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false); // Estado para colapso
  const [isMobileOpen, setIsMobileOpen] = useState(false); // Estado para visibilidade em dispositivos móveis
  const [searchQuery, setSearchQuery] = useState(''); // Estado para pesquisa
  const { updateMenu } = useNavigation();

  // Estado para armazenar a quantidade de alertas de vencimento
  const [alertCount, setAlertCount] = useState(0);

  // Busca os alertas assim que o componente for montado
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await api.get('/alertas');
        // Supondo que o endpoint retorne { alerts: [...] }
        if (response.data.alerts) {
          setAlertCount(response.data.alerts.length);
        }
      } catch (error) {
        console.error("Erro ao buscar alertas:", error);
      }
    };

    fetchAlerts();
  }, []);

  const handleLogout = () => {
    logout();
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    setSearchQuery(''); // Limpa a pesquisa ao colapsar/expandir
  };


  // Função para filtrar itens com base na pesquisa
  const filterItems = (items) => {
    if (!searchQuery.trim()) return items;
    return items.filter(item =>
      item.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
    );
  };

  // Itens filtrados (considera pesquisa e permissão)
  const filteredMenuItems = filterItems(menuItems).filter(item =>
    !item.permission || auth?.user?.permissions?.includes(item.permission)
  );
  const filteredSecondaryMenuItems = filterItems(secondaryMenuItems);

  return (
    <>
      {/* Botão de Toggle para Dispositivos Móveis */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2  text-white rounded-md focus:outline-none"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        aria-label="Toggle Sidebar"
      >
        <img src="/images/icon/menu.png" alt="Menu hamburguer" className="w-6 h-6" />
      </button>

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full bg-stone-100 flex flex-col justify-between transition-all duration-300 z-40 
          ${isCollapsed ? "w-20" : "w-auto"}
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full"} 
          md:translate-x-0 
          md:static 
          md:w-auto
        `}
      >
        {/* Cabeçalho da Sidebar */}
        <div className="w-full px-4 py-4 flex items-center justify-between">
          {/* Identidade da empresa em que se está operando.
              Era uma logo fixa da DAFMC: o elemento mais visível da tela dizia
              sempre a mesma empresa, e quem entrasse na MEMF continuava com a
              impressão de estar na DAFMC. Sem logo cadastrada, o lugar é do
              NOME da empresa — nunca de uma logo emprestada de outra. */}
          {!isCollapsed && (
            <div className="min-w-0" data-testid="empresa-sidebar">
              {empresa?.logoUrl ? (
                <img
                  className="w-auto h-[54px] max-w-[180px] object-contain"
                  src={config.urlArquivo(empresa.logoUrl)}
                  alt={empresa.nome}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : empresa ? (
                <div className="flex flex-col justify-center h-[54px] min-w-0">
                  <span className="text-[10px] uppercase tracking-wider text-stone-500 font-medium">
                    Empresa
                  </span>
                  <span className="text-stone-800 text-lg font-semibold leading-tight truncate max-w-[180px]">
                    {empresa.nome}
                  </span>
                </div>
              ) : (
                // Enquanto carrega, um esqueleto: melhor um vazio momentâneo do
                // que afirmar a empresa errada por um instante.
                <div className="h-[54px] flex items-center">
                  <span className="block h-6 w-32 bg-stone-200 rounded animate-pulse" aria-hidden="true" />
                </div>
              )}
            </div>
          )}

          {/* Colapsado, a identidade sobrevive nas iniciais da empresa. */}
          {isCollapsed && empresa?.nome && (
            <div
              className="w-10 h-10 rounded-md bg-stone-600 text-white flex items-center justify-center text-sm font-semibold"
              title={`Empresa: ${empresa.nome}`}
              data-testid="empresa-sidebar-colapsada"
            >
              {empresa.nome.trim().slice(0, 2).toUpperCase()}
            </div>
          )}
          {/* Botão de Colapso/Expandir */}
          {!isMobileOpen && (
            <button onClick={toggleSidebar} className="focus:outline-none cursor-pointer">
              <img
                className="w-6 h-6"
                src={
                  isCollapsed
                    ? "/images/icon/sidebar-open.png" // Ícone para expandir
                    : "/images/icon/sidebar-close.png" // Ícone para colapsar
                }
                alt={isCollapsed ? "Expandir Sidebar" : "Colapsar Sidebar"}
              />
            </button>
          )}
        </div>

        {/* Campo de Pesquisa */}
        {!isCollapsed && (
          <div className="flex items-center p-2 mx-4 mb-4 bg-white rounded-md border border-slate-300">
            <img
              className="w-5 h-5 mr-2"
              src="/images/icon/search.png"
              alt="Pesquisar"
            />
            <input
              type="text"
              placeholder="Pesquisar"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent text-sm text-[#1e1e1e] outline-none"
            />
          </div>
        )}

        {/* Navegação Principal */}
        <nav className="flex flex-col flex-grow w-full overflow-y-auto">
          {filteredMenuItems.length > 0 ? (
            filteredMenuItems.map((item, index) => (
              <NavLink
                key={index}
                to={item.to}
                className={({ isActive }) =>
                  `mx-4 self-stretch p-2 rounded-md justify-start items-center gap-4 inline-flex cursor-pointer hover:bg-gray-200 ${isActive ? "bg-blue-100 text-blue-800" : "text-stone-800"
                  } ${item.disabled ? "opacity-50 pointer-events-none" : ""}`
                }
                onClick={(e) => {
                  if (!item.disabled) {
                    updateMenu(item);
                    // Fechar a Sidebar em dispositivos móveis após a navegação
                    if (isMobileOpen) setIsMobileOpen(false);
                  } else {
                    e.preventDefault();
                  }
                }}
              >
                <img className="w-6 h-6 relative" src={item.icon} alt={`${item.name} Icon`} />
                {!isCollapsed && (
                  <div className="grow shrink basis-0 text-stone-800 text-base font-medium font-['Inter']">
                    {item.name}
                  </div>
                )}
                {<div className="w-2 h-10 rounded-full" />}
              </NavLink>
            ))
          ) : (
            <div className="self-stretch p-2 text-center text-gray-500">
              Nenhum resultado encontrado
            </div>
          )}

          {/* Linha de Separação após "Relatórios" */}
          {filteredMenuItems.length > 0 && (
            <div className="self-stretch h-[0px] border-t-2 border-stone-300 my-4"></div>
          )}

          {/* Navegação Secundária */}
          {filteredSecondaryMenuItems.length > 0 && (
            filteredSecondaryMenuItems.map((item, index) => (
              <NavLink
                key={index}
                to={item.to}
                className={({ isActive }) =>
                  `mx-4 self-stretch p-2 rounded-md justify-start items-center gap-4 inline-flex cursor-pointer hover:bg-gray-200 ${isActive ? "bg-blue-100 text-blue-800" : "text-stone-800"
                  }`
                }
                onClick={() => {
                  if (isMobileOpen) setIsMobileOpen(false);
                }}
              >
                <img className="w-6 h-6 relative" src={item.icon} alt={`${item.name} Icon`} />
                {!isCollapsed && (
                  <div className="grow shrink basis-0 text-stone-800 text-base font-medium font-['Inter'] flex items-center">
                    {item.name}
                    {/* Se o item for Alertas e houver alertas, mostra o badge */}
                    {item.name === "Alertas" && alertCount > 0 && (
                      <span className="ml-auto bg-red-500 text-white rounded-full text-xs px-2 py-0.5">
                        {alertCount}
                      </span>
                    )}
                  </div>
                )}
                {<div className="w-2 h-10 rounded-full" />}
              </NavLink>
            ))
          )}
        </nav>

        {/* Logout */}
        <div className="p-4 w-full">
          <button
            onClick={handleLogout}
            className="self-stretch p-2 rounded-md justify-start items-center gap-4 inline-flex hover:bg-gray-200 cursor-pointer"
          >
            <img className="w-6 h-6 relative" src="/images/icon/log-out.png" alt="Logout Icon" />
            {!isCollapsed && (
              <div className="grow shrink basis-0 text-stone-800 text-base font-medium font-['Inter']">
                Sair
              </div>
            )}
            {<div className="w-2 h-2 rounded-full" />}
          </button>
        </div>
      </div>

      {/* Overlay para dispositivos móveis quando a Sidebar está aberta */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        ></div>
      )}
    </>
  );
};

export default Sidebar;
